import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { TranslationKey } from '@/i18n-key';

const LaunchChangeKind = {
    NAME_CHANGED: 'nameChanged',
    CYCLE_START_CHANGED: 'cycleStartChanged',
    CYCLE_END_CHANGED: 'cycleEndChanged',
    AUDIENCE_CHANGED: 'audienceChanged',
    AUDIENCE_FILTERS_CHANGED: 'audienceFiltersChanged',
    EVALUATION_ADDED: 'evaluationAdded',
    EVALUATION_ADDED_NO_DATE: 'evaluationAddedNoDate',
    EVALUATION_REMOVED: 'evaluationRemoved',
    TEMPLATE_CHANGED: 'templateChanged',
    TEMPLATE_SET: 'templateSet',
    STEP_DATE_CHANGED: 'stepDateChanged',
    EMAIL_ENABLED: 'emailEnabled',
    EMAIL_DISABLED: 'emailDisabled',
    EMAIL_CONTENT_CHANGED: 'emailContentChanged',
} as const;

type LaunchChangeKindValue = (typeof LaunchChangeKind)[keyof typeof LaunchChangeKind];

const LaunchEmailRecipient = {
    EMPLOYEE: 'employee',
    MANAGER: 'manager',
} as const;

type LaunchEmailRecipientValue = (typeof LaunchEmailRecipient)[keyof typeof LaunchEmailRecipient];

interface MovedBoundary {
    from?: string;
    to?: string;
}

interface EvaluationScoped {
    evaluationType: EvaluationTypeValue;
    labelKey: TranslationKey;
}

interface StepScoped {
    stepId: string;
    labelKey: TranslationKey;
    isAlreadyTriggered: boolean;
}

interface NameChanged {
    kind: typeof LaunchChangeKind.NAME_CHANGED;
    from: string;
    to: string;
}

interface CycleStartChanged extends MovedBoundary {
    kind: typeof LaunchChangeKind.CYCLE_START_CHANGED;
}

interface CycleEndChanged extends MovedBoundary {
    kind: typeof LaunchChangeKind.CYCLE_END_CHANGED;
}

interface AudienceChanged {
    kind: typeof LaunchChangeKind.AUDIENCE_CHANGED;
    wasEntireCompany: boolean;
    isEntireCompany: boolean;
}

interface AudienceFiltersChanged {
    kind: typeof LaunchChangeKind.AUDIENCE_FILTERS_CHANGED;
}

interface EvaluationAdded extends EvaluationScoped {
    kind: typeof LaunchChangeKind.EVALUATION_ADDED;
    scheduledAt: string;
}

interface EvaluationAddedNoDate extends EvaluationScoped {
    kind: typeof LaunchChangeKind.EVALUATION_ADDED_NO_DATE;
}

interface EvaluationRemoved extends EvaluationScoped {
    kind: typeof LaunchChangeKind.EVALUATION_REMOVED;
}

interface TemplateChanged extends EvaluationScoped {
    kind: typeof LaunchChangeKind.TEMPLATE_CHANGED;
    from: string;
    to: string;
}

interface TemplateSet extends EvaluationScoped {
    kind: typeof LaunchChangeKind.TEMPLATE_SET;
    from: string;
    to: string;
}

interface StepDateChanged extends StepScoped, MovedBoundary {
    kind: typeof LaunchChangeKind.STEP_DATE_CHANGED;
}

interface EmailEnabled extends StepScoped {
    kind: typeof LaunchChangeKind.EMAIL_ENABLED;
    recipient: LaunchEmailRecipientValue;
}

interface EmailDisabled extends StepScoped {
    kind: typeof LaunchChangeKind.EMAIL_DISABLED;
    recipient: LaunchEmailRecipientValue;
}

interface EmailContentChanged extends StepScoped {
    kind: typeof LaunchChangeKind.EMAIL_CONTENT_CHANGED;
    recipient: LaunchEmailRecipientValue;
}

type LaunchEmailChange = EmailEnabled | EmailDisabled | EmailContentChanged;

type LaunchChange =
    | NameChanged
    | CycleStartChanged
    | CycleEndChanged
    | AudienceChanged
    | AudienceFiltersChanged
    | EvaluationAdded
    | EvaluationAddedNoDate
    | EvaluationRemoved
    | TemplateChanged
    | TemplateSet
    | StepDateChanged
    | LaunchEmailChange;

export { LaunchChangeKind, LaunchEmailRecipient };
export type {
    LaunchChange,
    LaunchChangeKindValue,
    LaunchEmailChange,
    LaunchEmailRecipientValue,
    StepDateChanged,
};
